import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * "What's changed since last visit" for a list of Horizon themes.
 *
 * Auto-snapshots the current themes to localStorage on each visit and diffs the
 * current data against the previous visit's snapshot. Pure client-side — every
 * field it needs already exists on the theme objects.
 *
 * Snapshot shape (per themeKey): { r: matrixRating, c: countToday, d: trendDirection }
 *
 * @param {string} storageKey  unique localStorage key for this page
 * @param {Array}  themes      current theme list
 * @returns {{ showChange, setShowChange, changeFor, summary }}
 *   - changeFor(theme) → null | { isNew, ratingChanged, volumeChanged, trendFlipped }
 *   - summary → { new, rerated, volume, trend } counts across all themes
 */
export default function useThemeChanges(storageKey, themes, version = "") {
  const [showChange, setShowChange] = useState(false);
  // Baseline = the snapshot from the PREVIOUS data version (e.g. yesterday's rollup). The stored
  // snapshot only advances when `version` changes — otherwise a reload/re-navigation the same day
  // would overwrite the baseline with the current data and the diff would show nothing.
  const [baseline, setBaseline] = useState(null);
  const writtenRef = useRef(false);

  useEffect(() => {
    if (writtenRef.current) return;
    if (!Array.isArray(themes) || themes.length === 0) return;

    const curr = {};
    themes.forEach((t) => {
      if (!t || !t.themeKey) return;
      curr[t.themeKey] = {
        r: t.llmTrend?.matrixRating || "",
        c: t.countToday || 0,
        d: t.trend7d?.direction || "",
      };
    });

    let stored = null;
    try {
      stored = JSON.parse(localStorage.getItem(storageKey) || "null");
    } catch {
      stored = null;
    }
    // Migrate an old flat snapshot (no `.v`) → treat it as the previous version's data.
    if (stored && stored.v === undefined) stored = { v: "", curr: stored, prev: {} };

    const write = (obj) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(obj));
      } catch {
        /* storage unavailable (private mode / quota) — ignore */
      }
    };

    if (!stored) {
      // First ever visit — nothing to flag; seed the snapshot.
      setBaseline({});
      write({ v: version, curr, prev: {} });
    } else if (version && stored.v === version) {
      // Same data version (reload / re-navigation) — keep the existing baseline, DON'T overwrite,
      // so the "since last visit" changes stay visible until genuinely new data arrives.
      setBaseline(stored.prev || {});
    } else {
      // New data version — diff against the last version's data, then advance the snapshot.
      setBaseline(stored.curr || {});
      write({ v: version, curr, prev: stored.curr || {} });
    }
    writtenRef.current = true;
  }, [storageKey, version, themes]);

  const isFlip = (a, b) =>
    (a === "increasing" && b === "decreasing") || (a === "decreasing" && b === "increasing");

  const changeFor = useCallback(
    (theme) => {
      if (!baseline || !theme || !theme.themeKey) return null;
      const prev = baseline[theme.themeKey];
      const curR = theme.llmTrend?.matrixRating || "";
      const curC = theme.countToday || 0;
      const curD = theme.trend7d?.direction || "";

      // Brand new theme (not present at all last visit). Only meaningful once we have
      // a non-empty baseline — on the very first ever visit nothing is flagged.
      if (!prev) {
        return Object.keys(baseline).length ? { isNew: true } : null;
      }

      const out = {};
      if (prev.r && curR && prev.r !== curR) out.ratingChanged = { from: prev.r, to: curR };
      const from = prev.c || 0;
      const diff = curC - from;
      if (Math.abs(diff) >= Math.max(5, from * 0.5)) out.volumeChanged = { from, to: curC, diff };
      if (prev.d && curD && isFlip(prev.d, curD)) out.trendFlipped = { from: prev.d, to: curD };

      return Object.keys(out).length ? out : null;
    },
    [baseline]
  );

  const summary = useMemo(() => {
    const s = { new: 0, rerated: 0, volume: 0, trend: 0 };
    if (!baseline || !Array.isArray(themes)) return s;
    themes.forEach((t) => {
      const c = changeFor(t);
      if (!c) return;
      if (c.isNew) s.new += 1;
      if (c.ratingChanged) s.rerated += 1;
      if (c.volumeChanged) s.volume += 1;
      if (c.trendFlipped) s.trend += 1;
    });
    return s;
  }, [baseline, themes, changeFor]);

  return { showChange, setShowChange, changeFor, summary };
}
